import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async Thunks
export const fetchBroadcastLists = createAsyncThunk(
  'broadcastLists/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcastLists.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBroadcastListById = createAsyncThunk(
  'broadcastLists/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcastLists.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createBroadcastList = createAsyncThunk(
  'broadcastLists/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcastLists.create(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateBroadcastList = createAsyncThunk(
  'broadcastLists/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcastLists.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addContactsToList = createAsyncThunk(
  'broadcastLists/addContacts',
  async ({ id, contacts }, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcastLists.addContacts(id, contacts);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeContactFromList = createAsyncThunk(
  'broadcastLists/removeContact',
  async ({ listId, contactId }, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcastLists.removeContact(listId, contactId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteBroadcastList = createAsyncThunk(
  'broadcastLists/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.broadcastLists.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  lists: [],
  currentList: null,
  loading: false,
  error: null,
  pagination: { page: 1, limit: 20, total: 0, pages: 1 }
};

const broadcastListsSlice = createSlice({
  name: 'broadcastLists',
  initialState,
  reducers: {
    setCurrentList: (state, action) => {
      state.currentList = action.payload;
    },
    clearCurrentList: (state) => {
      state.currentList = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchBroadcastLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBroadcastLists.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = action.payload.data || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchBroadcastLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by ID
      .addCase(fetchBroadcastListById.fulfilled, (state, action) => {
        state.currentList = action.payload.data || action.payload;
      })
      // Create
      .addCase(createBroadcastList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBroadcastList.fulfilled, (state, action) => {
        state.loading = false;
        const newList = action.payload.data || action.payload;
        state.lists.unshift(newList);
      })
      .addCase(createBroadcastList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateBroadcastList.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBroadcastList.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.data || action.payload;
        const idx = state.lists.findIndex(l => l._id === updated._id);
        if (idx !== -1) state.lists[idx] = updated;
        if (state.currentList?._id === updated._id) state.currentList = updated;
      })
      .addCase(updateBroadcastList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add contacts
      .addCase(addContactsToList.fulfilled, (state, action) => {
        const updated = action.payload.data || action.payload;
        const idx = state.lists.findIndex(l => l._id === updated._id);
        if (idx !== -1) state.lists[idx] = updated;
        if (state.currentList?._id === updated._id) state.currentList = updated;
      })
      // Remove contact
      .addCase(removeContactFromList.fulfilled, (state, action) => {
        const updated = action.payload.data || action.payload;
        const idx = state.lists.findIndex(l => l._id === updated._id);
        if (idx !== -1) state.lists[idx] = updated;
        if (state.currentList?._id === updated._id) state.currentList = updated;
      })
      // Delete
      .addCase(deleteBroadcastList.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBroadcastList.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = state.lists.filter(l => l._id !== action.payload);
      })
      .addCase(deleteBroadcastList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setCurrentList, clearCurrentList, clearError } = broadcastListsSlice.actions;
export default broadcastListsSlice.reducer;
